/**
 * Customer: account settings — deactivate the signed-in account.
 *
 * Backend: `app/api/v1/customer/account.py` (issue #361). The committed
 * `openapi.json` predates this endpoint, so the types are declared locally
 * (mirrors `app/schemas/account.py`). Replace with `Schemas[...]` once
 * `pnpm gen:types` catches up.
 *
 * Endpoint:
 *   POST /api/v1/customer/account/deactivate  { id_token } → { status: 'deactivated' }
 *
 * `id_token` is a Firebase ID token from an OTP sign-in with the account's own
 * mobile number, at most two minutes old. Deactivation keeps the data: the
 * account is refused at sign-in and on every request until support
 * reactivates it. Failures carry a stable `code` (ApiError.code):
 *   otp_invalid (400) · otp_too_old (400) · otp_for_other_number (403)
 *   account_deactivated (403) · account_not_found (404)
 */
import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import { ApiError, createKeyFactory } from '@finmin/api-client';
import { apiClient } from '@/lib/api';

// ---------- types -----------------------------------------------------------

export interface DeactivateAccountRequest {
  id_token: string;
}

export interface AccountStatusResponse {
  status: 'deactivated' | 'reactivated';
}

// ---------- query keys ------------------------------------------------------

export const accountKeys = createKeyFactory('account', {
  deactivate: () => ['deactivate'] as const,
});

// ---------- raw API functions -----------------------------------------------

/** POST /api/v1/customer/account/deactivate */
export async function deactivateAccount(payload: DeactivateAccountRequest, signal?: AbortSignal) {
  const { data } = await apiClient.post<AccountStatusResponse>(
    '/api/v1/customer/account/deactivate',
    payload,
    { signal },
  );
  return data;
}

// ---------- hooks -----------------------------------------------------------

/**
 * No cache to invalidate: on success the caller signs out, which clears the
 * whole query cache.
 */
export function useDeactivateAccount(
  options?: UseMutationOptions<AccountStatusResponse, ApiError, DeactivateAccountRequest>,
) {
  return useMutation<AccountStatusResponse, ApiError, DeactivateAccountRequest>({
    mutationKey: accountKeys.deactivate(),
    mutationFn: (payload) => deactivateAccount(payload),
    ...options,
  });
}
